import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';
import { useDrawer } from '../../contexts/DrawerContext';

interface ComplaintCategoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
  onSubCategorySelect?: (category: GrievanceCategory, subCategory: GrievanceSubCategory) => void;
}

interface GrievanceSubCategory {
  tckt_sb_ctgry_id: number;
  tckt_sb_ctgry_nm: string;
}

interface GrievanceCategory {
  tckt_ctgry_id: number;
  tckt_ctgry_nm: string;
  icn_tx?: string;
  tckt_sb_ctgry?: GrievanceSubCategory[];
}

export default function ComplaintCategoryDrawer({ isOpen, onClose, currentLanguage, onSubCategorySelect }: ComplaintCategoryDrawerProps) {
  const { openDrawer } = useDrawer();
  const [categories, setCategories] = useState<GrievanceCategory[]>([]);
  const [expandedCategoryId, setExpandedCategoryId] = useState<number | null>(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      setExpandedCategoryId(null);
      setCategoriesError(null);
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      setCategoriesError(null);

      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('x-access-token') ||
        sessionStorage.getItem('token') ||
        sessionStorage.getItem('x-access-token');

      const headers: HeadersInit = {};
      if (token) {
        headers['x-access-token'] = token;
      }

      const response = await service.get<GrievanceCategory[]>('/open/grievances/ticket/categories/sub-categories', {
        headers
      });

      if (response.status === 200 && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategoriesError(currentLanguage === 'en' ? 'Unable to load categories' : 'వర్గాలను లోడ్ చేయలేకపోయాం');
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setCategoriesError(
        err?.message ||
          (currentLanguage === 'en' ? 'Unable to load categories' : 'వర్గాలను లోడ్ చేయలేకపోయాం')
      );
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleCategoryToggle = (category: GrievanceCategory) => {
    const categoryName = category.tckt_ctgry_nm.toLowerCase().trim();
    
    // Check if this is "Hall and Arena bookings" category
    if (categoryName.includes('hall') && categoryName.includes('arena') && categoryName.includes('booking')) {
      // Open AI chat drawer with booking options
      onClose();
      // Set flag to trigger booking options in AI chat
      localStorage.setItem('showBookingOptions', 'true');
      openDrawer('ai-chat-drawer');
      return;
    }
    
    // Normal toggle behavior for other categories
    setExpandedCategoryId(prev => (prev === category.tckt_ctgry_id ? null : category.tckt_ctgry_id));
  };

  const handleSubCategoryClick = (category: GrievanceCategory, subCategory: GrievanceSubCategory) => {
    // Store in localStorage
    const grvnceDtls = {
      slctdCtgry: category.tckt_ctgry_id,
      slctdSbCtgry: subCategory.tckt_sb_ctgry_id,
      slctdCtgryNm: category.tckt_ctgry_nm,
      slctdSbCtgryNm: subCategory.tckt_sb_ctgry_nm
    };
    localStorage.setItem('grvnceDtls', JSON.stringify(grvnceDtls));

    // If custom handler is provided, use it; otherwise open chat drawer
    if (onSubCategorySelect) {
      onSubCategorySelect(category, subCategory);
      onClose();
    } else {
      // Close category drawer and open chat drawer
      onClose();
      openDrawer('chat-drawer');
    }
  };

  const getCategoryIcon = (categoryName: string, iconPath?: string) => {
    // If iconPath is provided and is a full URL, use it
    if (iconPath && iconPath.startsWith('http')) return iconPath;
    
    // Map category names to icon files in assets/images/categories
    const categoryIconMap: { [key: string]: string } = {
      'BUILDING PLAN PERMISSION': '/assets/images/categories/townplan.png',
      'GARBAGE': '/assets/images/categories/grbgeclct.png',
      'GENERAL': '/assets/images/categories/general.png',
      'GOVERNMENT SCHEMES': '/assets/images/categories/voter.png',
      'PUBLIC HEALTH': '/assets/images/categories/public.png',
      'ROADS AND FOOTPATHS': '/assets/images/categories/Pot_holes.png',
      'STREET LIGHTS': '/assets/images/categories/streetlight.png',
      'TAXES AND LICENCES': '/assets/images/categories/tax.png',
      'WATER STAGNATION': '/assets/images/categories/drainage.png',
      'WATER SUPPLY': '/assets/images/categories/water-tap.png',
      'AIR POLLUTION': '/assets/images/categories/public.png',
      'BIN POINTS': '/assets/images/categories/grbgeclct.png',
      'PARKS': '/assets/images/categories/park.png',
    };

    // Try to find exact match
    const upperName = categoryName.toUpperCase();
    if (categoryIconMap[upperName]) {
      return categoryIconMap[upperName];
    }

    // Try partial match
    for (const [key, value] of Object.entries(categoryIconMap)) {
      if (upperName.includes(key) || key.includes(upperName)) {
        return value;
      }
    }

    // If iconPath is provided, try to use it
    if (iconPath) {
      if (iconPath.startsWith('http') || iconPath.startsWith('/')) return iconPath;
      return `/assets/images/categories/${iconPath}`;
    }

    // Default fallback
    return '/assets/images/categories/general.png';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[10000]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[10000] shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-4 flex items-center justify-between shadow">
              <p className="text-sm font-bold uppercase tracking-wide flex-1">
                {currentLanguage === 'en' ? 'Select Complaint Category' : 'ఫిర్యాదు వర్గాన్ని ఎంచుకోండి'}
              </p>
              <button
                onClick={onClose}
                className="text-white hover:text-indigo-100 transition p-1"
                aria-label="Close category drawer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Banner with Cartoon Illustration */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-700 px-5 py-4 flex items-center gap-4">
              <div className="flex-shrink-0">
                <img
                  src="/assets/images/categories/general.png"
                  alt="RMC Sevak"
                  className="w-16 h-16 object-contain"
                  onError={(e) => {
                    // Try alternative paths
                    const altPaths = [
                      'https://rmc.ap.gov.in/assets/img/home/rjy.png',
                      '/assets/img/home/rjy.png'
                    ];
                    const currentSrc = (e.currentTarget as HTMLImageElement).src;
                    const currentIndex = altPaths.findIndex(path => currentSrc.includes(path));
                    if (currentIndex < altPaths.length - 1) {
                      (e.currentTarget as HTMLImageElement).src = altPaths[currentIndex + 1];
                    }
                  }}
                />
              </div>
              <p className="text-white text-sm font-medium flex-1 leading-relaxed">
                {currentLanguage === 'en'
                  ? 'Please select your problem category from the below lists ...'
                  : 'దయచేసి క్రింది జాబితా నుండి మీ సమస్య వర్గాన్ని ఎంచుకోండి'}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto bg-white">
              {isLoadingCategories ? (
                <div className="flex items-center justify-center h-full text-indigo-600">
                  <i className="ri-loader-4-line animate-spin text-3xl"></i>
                </div>
              ) : categoriesError ? (
                <div className="p-6 text-center text-red-600 text-sm">
                  {categoriesError}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {categories.map((category) => {
                    const isExpanded = expandedCategoryId === category.tckt_ctgry_id;
                    return (
                      <div key={category.tckt_ctgry_id} className="px-4 hover:bg-gray-50 transition-colors">
                        <button
                          onClick={() => handleCategoryToggle(category)}
                          className="w-full flex items-center justify-between py-4 focus:outline-none group"
                        >
                          <div className="flex items-center gap-3 text-left flex-1">
                            <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center overflow-hidden shadow-sm group-hover:border-indigo-400 transition-colors">
                              <img
                                src={getCategoryIcon(category.tckt_ctgry_nm, category.icn_tx)}
                                alt={category.tckt_ctgry_nm}
                                className="w-10 h-10 object-contain p-1"
                                onError={(e) => {
                                  // Try fallback paths
                                  const fallbackPaths = [
                                    '/assets/images/categories/general.png',
                                    'https://rmc.ap.gov.in/assets/img/home/rjy.png',
                                    '/assets/img/home/rjy.png'
                                  ];
                                  const currentSrc = (e.currentTarget as HTMLImageElement).src;
                                  const currentIndex = fallbackPaths.findIndex(path => currentSrc.includes(path));
                                  if (currentIndex < fallbackPaths.length - 1) {
                                    (e.currentTarget as HTMLImageElement).src = fallbackPaths[currentIndex + 1];
                                  } else {
                                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                                  }
                                }}
                              />
                            </div>
                            <span className="text-sm font-semibold text-gray-800 uppercase tracking-wide group-hover:text-indigo-600 transition-colors">
                              {category.tckt_ctgry_nm}
                            </span>
                          </div>
                          <i
                            className={`ri-add-line text-2xl transition-all ${
                              isExpanded ? 'rotate-45 text-indigo-600' : 'text-gray-400 group-hover:text-indigo-500'
                            }`}
                          ></i>
                        </button>

                        <AnimatePresence>
                          {isExpanded && category.tckt_sb_ctgry && category.tckt_sb_ctgry.length > 0 && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="pb-4 pl-4 space-y-2"
                            >
                              {category.tckt_sb_ctgry.map((sub) => (
                                <motion.div
                                  key={sub.tckt_sb_ctgry_id}
                                  onClick={() => handleSubCategoryClick(category, sub)}
                                  className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-indigo-50 hover:border-indigo-300 transition-all group"
                                  whileHover={{ x: 4 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <i className="ri-arrow-right-s-line text-indigo-600 group-hover:translate-x-1 transition-transform"></i>
                                  <span className="text-sm text-gray-700 group-hover:text-indigo-700 font-medium">{sub.tckt_sb_ctgry_nm}</span>
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

